/**
 * Library snapshot builders / appliers (Block L1).
 *
 * Source of truth — UI store, не наоборот. Если store выставляет X — кладём X
 * в snapshot; на apply — записываем snapshot в store через существующие setters.
 *
 * Snapshot v1 (matches backend src/schemas/library.py):
 *   { schema_version: 1, ... }
 *
 * Column identity strategy: match by name. Missing columns → warnings[].
 * НЕ делаем embeddings / fuzzy match.
 *
 * Block S1: Search v2 добавил priority_domains (list[str]) в snapshot
 * (тиер — economy/optimal/max). Snapshot остаётся v1 (libraries).
 */
import { useSearchStore } from '@/store/searchStore';
import { useGenerationStore } from '@/store/generationStore';
import { useMatchingStore } from '@/store/matchingStore';
import type {
  ManualPropertyDraft,
  MultivalueDelimiter,
} from '@/store/matchingStore';
import { useFilesTransformStore } from '@/store/filesTransformStore';

export const SNAPSHOT_SCHEMA_VERSION = 1;

/**
 * P1.4 (2026-08-14): unified workspace library snapshot.
 *
 * schema_version 2 = composite: { schema_version: 2, search?, generation?,
 * matching?, files_transform? }, где каждая секция — существующий v1 snapshot
 * (со своим schema_version: 1). Секции опциональны (отсутствие = не покрыто).
 */
export const WORKSPACE_SNAPSHOT_SCHEMA_VERSION = 2;

// =================== Search ===================

export function buildSearchLibrarySnapshot(state: {
  datasetId: string | null;
  selectedColumns: string[];
  outputLanguage: string;
  tierKey?: 'economy' | 'optimal' | 'max';
  priorityDomains?: string[];
}): Record<string, unknown> {
  return {
    schema_version: SNAPSHOT_SCHEMA_VERSION,
    dataset_id: state.datasetId,
    selected_column_keys: [...state.selectedColumns],
    output_language: state.outputLanguage || 'Russian',
    tier_key: state.tierKey ?? 'optimal',
    priority_domains: [...(state.priorityDomains ?? [])],
    // Spec § B12 — зарезервировано; дефолт false до Phase #9 spike.
    prom_mode: false,
  };
}

export interface ApplyResult {
  warnings: string[];
}

export function applySearchLibrarySnapshot(
  store: ReturnType<typeof useSearchStore.getState>,
  snapshot: Record<string, unknown>,
  availableColumnNames?: string[],
): ApplyResult {
  const warnings: string[] = [];
  const sv = snapshot.schema_version;
  if (sv !== SNAPSHOT_SCHEMA_VERSION) {
    warnings.push(`snapshot.schema_version=${sv} is not supported, expected 1`);
  }
  if (typeof snapshot.dataset_id === 'string' && snapshot.dataset_id) {
    store.setDatasetId(snapshot.dataset_id);
  }
  if (typeof snapshot.output_language === 'string' && snapshot.output_language) {
    store.setOutputLanguage(snapshot.output_language);
  }
  const tier = snapshot.tier_key;
  if (tier === 'economy' || tier === 'optimal' || tier === 'max') {
    store.setTierKey(tier);
  } else if (tier !== undefined) {
    warnings.push(`Unknown tier_key=${JSON.stringify(tier)}`);
  }
  if (Array.isArray(snapshot.priority_domains)) {
    const domains = (snapshot.priority_domains as unknown[])
      .filter((d): d is string => typeof d === 'string' && d.trim().length > 0)
      .map((d) => d.trim().toLowerCase());
    store.setPriorityDomains(domains);
  }
  const colsRaw = snapshot.selected_column_keys;
  if (Array.isArray(colsRaw)) {
    const requested = colsRaw.filter((c): c is string => typeof c === 'string');
    if (availableColumnNames && availableColumnNames.length > 0) {
      const available = new Set(availableColumnNames);
      const matched = requested.filter((c) => available.has(c));
      const missing = requested.filter((c) => !available.has(c));
      for (const m of missing) {
        warnings.push(`Column ${JSON.stringify(m)} not found in dataset, skipped`);
      }
      store.setSelectedColumns(matched);
    } else {
      store.setSelectedColumns(requested);
    }
  }
  return { warnings };
}

// =================== Generation ===================

export function buildGenerationLibrarySnapshot(state: {
  datasetId: string | null;
  selectedColumns: string[];
  tierKey: 'economy' | 'optimal' | 'max';
  outputLanguage: string;
  moduleKeys: string[];
  temperature: number;
  maxTokens: number;
  testRunLimit: number;
  isTestRun: boolean;
}): Record<string, unknown> {
  return {
    schema_version: SNAPSHOT_SCHEMA_VERSION,
    dataset_id: state.datasetId,
    selected_column_keys: [...state.selectedColumns],
    tier_key: state.tierKey,
    enabled_module_keys: [...state.moduleKeys],
    output_language: state.outputLanguage || 'Russian',
    temperature: state.temperature,
    max_tokens: state.maxTokens,
    test_run_limit: state.testRunLimit,
    is_test_run: state.isTestRun,
  };
}

export function applyGenerationLibrarySnapshot(
  store: ReturnType<typeof useGenerationStore.getState>,
  snapshot: Record<string, unknown>,
  availableColumnNames?: string[],
): ApplyResult {
  const warnings: string[] = [];
  const sv = snapshot.schema_version;
  if (sv !== SNAPSHOT_SCHEMA_VERSION) {
    warnings.push(`snapshot.schema_version=${sv} is not supported, expected 1`);
  }
  if (typeof snapshot.dataset_id === 'string' && snapshot.dataset_id) {
    store.setDatasetId(snapshot.dataset_id);
  }
  const tier = snapshot.tier_key;
  if (tier === 'economy' || tier === 'optimal' || tier === 'max') {
    store.setTierKey(tier);
  } else if (tier !== undefined) {
    warnings.push(`Unknown tier_key=${JSON.stringify(tier)}`);
  }
  if (typeof snapshot.output_language === 'string' && snapshot.output_language) {
    store.setOutputLanguage(snapshot.output_language);
  }
  if (Array.isArray(snapshot.enabled_module_keys)) {
    store.setModuleKeys(snapshot.enabled_module_keys.filter((k): k is string => typeof k === 'string'));
  }
  if (typeof snapshot.temperature === 'number') {
    store.setTemperature(snapshot.temperature);
  }
  if (typeof snapshot.max_tokens === 'number') {
    store.setMaxTokens(snapshot.max_tokens);
  }
  if (typeof snapshot.test_run_limit === 'number') {
    store.setTestRunLimit(snapshot.test_run_limit);
  }
  if (typeof snapshot.is_test_run === 'boolean') {
    store.setIsTestRun(snapshot.is_test_run);
  }
  const colsRaw = snapshot.selected_column_keys;
  if (Array.isArray(colsRaw)) {
    const requested = colsRaw.filter((c): c is string => typeof c === 'string');
    if (availableColumnNames && availableColumnNames.length > 0) {
      const available = new Set(availableColumnNames);
      const matched = requested.filter((c) => available.has(c));
      const missing = requested.filter((c) => !available.has(c));
      for (const m of missing) {
        warnings.push(`Column ${JSON.stringify(m)} not found in dataset, skipped`);
      }
      store.setSelectedColumns(matched);
    } else {
      store.setSelectedColumns(requested);
    }
  }
  return { warnings };
}

// =================== Matching ===================
//
// Block M1a-FE (TZ 2026-07-29): composition snapshot.
// Shape совпадает с backend src/schemas/library.py::MatchingSnapshotV1.
// Никаких секретов — denylist уже в BE.

export function buildMatchingLibrarySnapshot(state: {
  datasetId: string | null;
  templateId: string | null;
  language: 'ua' | 'ru';
  manualProperties: ManualPropertyDraft[];
  selectedInputColumns: string[];
  multivalueDelimiter: MultivalueDelimiter;
  tierKey: 'economy' | 'optimal' | 'max';
}): Record<string, unknown> {
  return {
    schema_version: SNAPSHOT_SCHEMA_VERSION,
    dataset_id: state.datasetId,
    template_id: state.templateId,
    language: state.language,
    manual_properties: state.manualProperties.map((p) => ({
      name: p.name,
      unit: p.unit,
      allowed_values: p.allowedValues,
    })),
    matching_input_columns: [...state.selectedInputColumns],
    multivalue_delimiter: state.multivalueDelimiter,
    tier_key: state.tierKey,
    matching_stage: 'categories',
  };
}

export function applyMatchingLibrarySnapshot(
  store: ReturnType<typeof useMatchingStore.getState>,
  snapshot: Record<string, unknown>,
  availableColumnNames?: string[],
): ApplyResult {
  const warnings: string[] = [];
  const sv = snapshot.schema_version;
  if (sv !== SNAPSHOT_SCHEMA_VERSION) {
    warnings.push(`snapshot.schema_version=${sv} is not supported, expected 1`);
  }
  if (typeof snapshot.dataset_id === 'string' && snapshot.dataset_id) {
    store.setTemplateId(null);
  }
  if (typeof snapshot.template_id === 'string') {
    store.setTemplateId(snapshot.template_id || null);
  } else if (snapshot.template_id === null) {
    store.setTemplateId(null);
  }
  if (snapshot.language === 'ua' || snapshot.language === 'ru') {
    store.setLanguage(snapshot.language);
  } else if (snapshot.language !== undefined) {
    warnings.push(`Unknown language=${JSON.stringify(snapshot.language)}`);
  }
  const tier = snapshot.tier_key;
  if (tier === 'economy' || tier === 'optimal' || tier === 'max') {
    store.setTierKey(tier);
  } else if (tier !== undefined) {
    warnings.push(`Unknown tier_key=${JSON.stringify(tier)}`);
  }

  if (Array.isArray(snapshot.manual_properties)) {
    const props: ManualPropertyDraft[] = [];
    const rawList = snapshot.manual_properties as unknown[];
    for (let i = 0; i < rawList.length && i < 8; i++) {
      const raw = rawList[i];
      if (!raw || typeof raw !== 'object') continue;
      const r = raw as Record<string, unknown>;
      if (typeof r.name !== 'string' || r.name.trim() === '') {
        warnings.push(`manual_property[${i}] missing name, skipped`);
        continue;
      }
      const unitRaw = r.unit;
      const allowedRaw = r.allowed_values;
      props.push({
        id:
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `mp_lb_${Date.now()}_${i}`,
        name: r.name.trim().slice(0, 64),
        unit:
          typeof unitRaw === 'string' && unitRaw.trim() ? unitRaw.trim() : null,
        allowedValues: Array.isArray(allowedRaw)
          ? (allowedRaw.filter(
              (v): v is string => typeof v === 'string' && v.trim().length > 0,
            ) as string[])
          : null,
        // §D.4 manual modes: snapshots carry the allowed-values shape only → only_mine
        mode: 'only_mine',
        formatRule: null,
      });
    }
    store.setManualProperties(props);
  }

  const colsRaw = snapshot.matching_input_columns;
  if (Array.isArray(colsRaw)) {
    const requested = colsRaw.filter((c): c is string => typeof c === 'string');
    if (availableColumnNames && availableColumnNames.length > 0) {
      const available = new Set(availableColumnNames);
      const matched = requested.filter((c) => available.has(c));
      const missing = requested.filter((c) => !available.has(c));
      for (const m of missing) {
        warnings.push(`Column ${JSON.stringify(m)} not found in dataset, skipped`);
      }
      store.setSelectedInputColumns(matched);
    } else {
      store.setSelectedInputColumns(requested);
    }
  }

  const d = snapshot.multivalue_delimiter;
  if (d === ',' || d === ';' || d === '|' || d === '\n') {
    store.setMultivalueDelimiter(d);
  } else if (d !== undefined) {
    warnings.push(`Unknown multivalue_delimiter=${JSON.stringify(d)}`);
  }

  return { warnings };
}

// =================== Files Transform ===================
//
// Phase 2.3 (TZ 2026-08-06): Transform form snapshots.
//
// Source of truth — UI state (pickedSheet + renames + selected).
// Никакого dataset_id в snapshot — по TZ «library saves Transform config
// separately from raw data». Идентификация конкретного dataset
// передаётся body в /apply.
//
// Snapshot v1 shape:
//   { schema_version: 1, selected_sheet, column_renames, selected_columns }
//
// Apply: возвращает НЕ void, а ApplyResult с warnings — FE дёргает
// useSelectSheet / useRenameColumns / useSelectColumns и хочет знать,
// какие warnings пришли с бэка (missing sheet / missing columns).
// Сами warnings в store не пишем — UI рендерит их в LibraryBar.

export function buildFilesTransformLibrarySnapshot(state: {
  selectedSheet: string | null;
  renames: Record<string, string>;
  selected: string[];
}): Record<string, unknown> {
  return {
    schema_version: SNAPSHOT_SCHEMA_VERSION,
    selected_sheet: state.selectedSheet ?? null,
    column_renames: { ...state.renames },
    selected_columns: [...state.selected],
  };
}

export function applyFilesTransformLibrarySnapshot(
  // Mutator-set: any object exposing these setters. FilesTransformPage
  // держит state в local React state (не в Zustand), поэтому принимаем
  // явный интерфейс.
  mutators: {
    setPickedSheet: (sheet: string | null) => void;
    setRenames: (renames: Record<string, string>) => void;
    setSelected: (selected: string[]) => void;
  },
  snapshot: Record<string, unknown>,
  availableColumnNames?: string[],
): ApplyResult {
  const warnings: string[] = [];
  const sv = snapshot.schema_version;
  if (sv !== SNAPSHOT_SCHEMA_VERSION) {
    warnings.push(`snapshot.schema_version=${sv} is not supported, expected 1`);
  }

  if (snapshot.selected_sheet === null || snapshot.selected_sheet === undefined) {
    // null/undefined → оставляем текущее (не трогаем). Apply мог быть сделан
    // на dataset без выбранного sheet'а — мы не хотим «обнулить» выбор.
  } else if (typeof snapshot.selected_sheet === 'string') {
    mutators.setPickedSheet(snapshot.selected_sheet);
  } else {
    warnings.push(`Unknown selected_sheet=${JSON.stringify(snapshot.selected_sheet)}`);
  }

  if (snapshot.column_renames && typeof snapshot.column_renames === 'object') {
    const raw = snapshot.column_renames as Record<string, unknown>;
    const renames: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw)) {
      if (typeof v === 'string') {
        renames[k] = v;
      }
    }
    mutators.setRenames(renames);
  }

  if (Array.isArray(snapshot.selected_columns)) {
    const requested = (snapshot.selected_columns as unknown[]).filter(
      (c): c is string => typeof c === 'string',
    );
    if (availableColumnNames && availableColumnNames.length > 0) {
      const available = new Set(availableColumnNames);
      const matched = requested.filter((c) => available.has(c));
      const missing = requested.filter((c) => !available.has(c));
      for (const m of missing) {
        warnings.push(`Column ${JSON.stringify(m)} not found in dataset, skipped`);
      }
      mutators.setSelected(matched);
    } else {
      mutators.setSelected(requested);
    }
  }

  return { warnings };
}

// =================== Workspace (unified) ===================
//
// P1.4 (2026-08-14): composite snapshot covering all 4 tabs.
// Существующие per-kind build/apply переиспользуются as-is — здесь только
// сборка/разбор composite-обёртки и reset всех stores.

/**
 * Собирает composite workspace snapshot из текущего state всех 4 stores.
 * Секции всегда присутствуют (build* детерминирован); отсутствующие секции
 * бывают только у legacy-библиотек, которые оборачивает WorkspaceLibraryBar.
 */
export function buildWorkspaceSnapshot(): Record<string, unknown> {
  const s = useSearchStore.getState();
  const g = useGenerationStore.getState();
  const m = useMatchingStore.getState();
  const f = useFilesTransformStore.getState();

  return {
    schema_version: WORKSPACE_SNAPSHOT_SCHEMA_VERSION,
    search: buildSearchLibrarySnapshot({
      datasetId: s.datasetId,
      selectedColumns: s.selectedColumns,
      outputLanguage: s.outputLanguage,
      tierKey: s.tierKey,
      priorityDomains: s.priorityDomains,
    }),
    generation: buildGenerationLibrarySnapshot({
      datasetId: g.datasetId,
      selectedColumns: g.selectedColumns,
      tierKey: g.tierKey,
      outputLanguage: g.outputLanguage,
      moduleKeys: g.moduleKeys,
      temperature: g.temperature,
      maxTokens: g.maxTokens,
      testRunLimit: g.testRunLimit,
      isTestRun: g.isTestRun,
    }),
    matching: buildMatchingLibrarySnapshot({
      // matchingStore намеренно не хранит datasetId (cross-project bleed) —
      // для workspace snapshot кладём null; реальный dataset подставляется
      // страницей при запуске пайплайна.
      datasetId: null,
      templateId: m.templateId,
      language: m.language,
      manualProperties: m.manualProperties,
      selectedInputColumns: m.selectedInputColumns,
      multivalueDelimiter: m.multivalueDelimiter,
      tierKey: m.tierKey,
    }),
    files_transform: buildFilesTransformLibrarySnapshot({
      selectedSheet: f.pickedSheet,
      renames: f.renames,
      selected: f.selectedColumns,
    }),
  };
}

/**
 * Применяет composite (schema_version 2) snapshot к stores.
 *
 * CONTRACT: принимает ТОЛЬКО schema_version === 2. Legacy flat (v1) snapshot
 * вызывающий WorkspaceLibraryBar оборачивает сам как
 * `{ schema_version: 2, [kind]: snapshot }` — он знает `kind` из list-item.
 *
 * availableColumnNames НЕ передаём (apply применяет все запрошенные колонки;
 * фильтрация по реальным колонкам датасета — ответственность UI / pages).
 *
 * Returns: объединённые warnings всех секций.
 */
export function applyWorkspaceSnapshot(snapshot: Record<string, unknown>): string[] {
  const warnings: string[] = [];
  if (snapshot.schema_version !== WORKSPACE_SNAPSHOT_SCHEMA_VERSION) {
    warnings.push(
      `snapshot.schema_version=${snapshot.schema_version} is not supported, expected 2`,
    );
    return warnings;
  }

  const searchSection = snapshot.search;
  if (searchSection && typeof searchSection === 'object') {
    warnings.push(
      ...applySearchLibrarySnapshot(
        useSearchStore.getState(),
        searchSection as Record<string, unknown>,
      ).warnings,
    );
  }

  const generationSection = snapshot.generation;
  if (generationSection && typeof generationSection === 'object') {
    warnings.push(
      ...applyGenerationLibrarySnapshot(
        useGenerationStore.getState(),
        generationSection as Record<string, unknown>,
      ).warnings,
    );
  }

  const matchingSection = snapshot.matching;
  if (matchingSection && typeof matchingSection === 'object') {
    warnings.push(
      ...applyMatchingLibrarySnapshot(
        useMatchingStore.getState(),
        matchingSection as Record<string, unknown>,
      ).warnings,
    );
  }

  const ftSection = snapshot.files_transform;
  if (ftSection && typeof ftSection === 'object') {
    // applyFilesTransformLibrarySnapshot ждёт mutators { setPickedSheet,
    // setRenames, setSelected }; store экспонирует setSelectedColumns —
    // адаптируем имя.
    const ft = useFilesTransformStore.getState();
    warnings.push(
      ...applyFilesTransformLibrarySnapshot(
        {
          setPickedSheet: ft.setPickedSheet,
          setRenames: ft.setRenames,
          setSelected: ft.setSelectedColumns,
        },
        ftSection as Record<string, unknown>,
      ).warnings,
    );
  }

  return warnings;
}

/**
 * Сбрасывает все 4 store к дефолтам (workspace «Reset»).
 */
export function resetWorkspaceState(): void {
  useSearchStore.getState().reset();
  useGenerationStore.getState().reset();
  useMatchingStore.getState().reset();
  useFilesTransformStore.getState().reset();
}

